<script lang="ts">
	import { Card } from "#lib/web-ui/ui/card/index.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import {
		Collapsible,
		CollapsibleContent,
		CollapsibleTrigger
	} from "#lib/web-ui/ui/collapsible/index.js";
	import { Checkbox } from "#lib/web-ui/ui/checkbox/index.js";
	import { Switch } from "#lib/web-ui/ui/switch/index.js";
	import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
	import currency from "currency.js";
	import type Currency from "currency.js";
	import { PieChart, Tooltip as ChartTooltip } from "layerchart";
	import { toast } from "svelte-sonner";
	import InfoPopover from "./mortgage-info-popover.svelte";
	import DatePicker from "./mortgage-date-picker.svelte";
	import { CalendarDate } from "@internationalized/date";
	import AmortizationTable from "./mortgage-amortization-table.svelte";

	let {
		price = 250000
	}: {
		price?: number;
	} = $props();

	// TODO: Future load through settings UI for dynamic config
	const initialValues = {
		mortgageInsuranceRate: 0.52,
		downPaymentPercent: 20,
		annualInterestRate: 6.5,
		loanTerm: 30,
		propertyTaxRate: 1.89,
		homeInsuranceRate: 0.5,
		annualHoaDues: 0,
		startDate: new Date()
	};

	// Defer PieChart render to avoid layerchart reactive context race on iOS WebKit
	// during SvelteKit client-side navigation (sveltejs/svelte#16548)
	let mounted = $state(false);
	$effect(() => {
		mounted = true;
	});

	let showAdvanced = $state(false);
	let showAmortization = $state(false);
	let includePMI = $state(true);
	let includeTaxesInsurance = $state(true);

	// Amortization Start Date
	let startDate = $state(
		new CalendarDate(
			initialValues.startDate.getFullYear(),
			initialValues.startDate.getMonth(),
			initialValues.startDate.getDate()
		)
	);

	type MortgageFormState = {
		price: Currency;
		loanTerm: number;
		interestRate: number;
		mortgageInsuranceRate: number;
		downPaymentAmount: Currency;
		downPaymentPercent: Currency;
		annualHomeInsurance: Currency;
		monthlyHomeInsurance: Currency;
		annualPropertyTaxRate: Currency;
		annualPropertyTax: Currency;
		annualHoaDues: Currency;
		monthlyHoaDues: Currency;
		monthlyPropertyTax: Currency;
		[key: string]: Currency | number; // Add index signature to allow dynamic property access
	};

	// Form state initialized from defaults
	let formValues = $state({
		price: currency(price),
		loanTerm: initialValues.loanTerm,
		interestRate: initialValues.annualInterestRate,
		mortgageInsuranceRate: initialValues.mortgageInsuranceRate,
		downPaymentAmount: currency(initialValues.downPaymentPercent / 100).multiply(price),
		downPaymentPercent: currency(initialValues.downPaymentPercent),
		annualHomeInsurance: currency((initialValues.homeInsuranceRate / 100) * price),
		monthlyHomeInsurance: currency((initialValues.homeInsuranceRate / 100) * price).divide(12),
		annualPropertyTaxRate: currency(initialValues.propertyTaxRate),
		annualPropertyTax: currency((initialValues.propertyTaxRate / 100) * price),
		annualHoaDues: currency(initialValues.annualHoaDues),
		monthlyHoaDues: currency(initialValues.annualHoaDues).divide(12),
		monthlyPropertyTax: currency((initialValues.propertyTaxRate / 100) * price).divide(12)
	});

	const loanAmount = $derived(
		currency(formValues.price).subtract(
			currency(formValues.price).multiply(
				currency(formValues.downPaymentPercent).divide(currency(100))
			)
		)
	);

	// Input validation and state update handler
	function stateHandler(e: FocusEvent | KeyboardEvent) {
		if (e instanceof KeyboardEvent && e.key !== "Enter") return;
		const { id, value } = e.target as HTMLInputElement;

		const num = Number(value.replace(/[^0-9.-]/g, ""));
		const previousValue = formValues[id as keyof typeof formValues];

		// Validation checks
		if (id === "loanAmount" && num > Number(formValues.price)) {
			toast.error("Loan amount cannot exceed purchase price");
			(e.target as HTMLInputElement).value = currency(previousValue).format();
			return;
		}

		if (Number.isNaN(num) || num < 0) {
			toast.error("Value cannot be negative");
			(e.target as HTMLInputElement).value = currency(previousValue).format();
			return;
		}

		if (
			[
				"interestRate",
				"downPaymentPercent",
				"mortgageInsuranceRate",
				"annualPropertyTaxRate"
			].includes(id)
		) {
			if (num > 100) {
				toast.error("Percentage cannot exceed 100%");
				(e.target as HTMLInputElement).value =
					`${currency(previousValue).format({ symbol: "" })} %`;
				return;
			}
		}

		(formValues as MortgageFormState)[id as keyof MortgageFormState] = currency(value);

		// Update related fields
		switch (id) {
			case "annualHoaDues":
				formValues.monthlyHoaDues = currency(value).divide(12);
				break;
			case "monthlyHoaDues":
				formValues.annualHoaDues = currency(value).multiply(12);
				break;
			case "annualHomeInsurance":
				formValues.monthlyHomeInsurance = currency(value).divide(12);
				break;
			case "annualPropertyTaxRate":
				formValues.annualPropertyTax = currency(value).multiply(price).divide(100);
				formValues.monthlyPropertyTax = currency(value)
					.multiply(price)
					.divide(100)
					.divide(12);
				break;
			case "annualPropertyTax":
				formValues.monthlyPropertyTax = currency(value).divide(12);
				formValues.annualPropertyTaxRate = currency(value).divide(price).multiply(100);
				break;
			case "downPaymentAmount":
				formValues.downPaymentPercent = currency(value).divide(price).multiply(100);
				break;
			case "downPaymentPercent":
				formValues.downPaymentAmount = currency(value).divide(100).multiply(price);
				break;
		}
	}

	// Calculated monthly payments
	// PMI is required when down payment is less than 20%
	const monthlyPMI = $derived(
		includePMI && Number(formValues.downPaymentPercent) < 20
			? currency(loanAmount).multiply(formValues.mortgageInsuranceRate).divide(100).divide(12)
			: 0
	);

	// Monthly mortgage payment calculation using standard amortization formula
	const monthlyPrincipalInterestPayment = $derived(
		formValues.interestRate === 0
			? currency(loanAmount).divide(formValues.loanTerm * 12)
			: currency(loanAmount)
					.multiply(formValues.interestRate / 100 / 12)
					.multiply(
						(1 + formValues.interestRate / 100 / 12) ** (formValues.loanTerm * 12)
					)
					.divide(
						(1 + formValues.interestRate / 100 / 12) ** (formValues.loanTerm * 12) - 1
					)
	);

	// Total monthly payment including all expenses
	const totalMonthlyPayment = $derived(
		currency(monthlyPrincipalInterestPayment)
			.add(currency(includeTaxesInsurance ? formValues.monthlyPropertyTax : 0))
			.add(currency(includeTaxesInsurance ? formValues.monthlyHomeInsurance : 0))
			.add(currency(includeTaxesInsurance ? formValues.monthlyHoaDues : 0))
			.add(currency(includePMI ? monthlyPMI : 0))
	);

	// Chart data for visualization
	const chartData = $derived(
		[
			{
				label: "Principal & Interest",
				value: Number(monthlyPrincipalInterestPayment),
				color: "hsl(222, 47%, 31%)"
			},
			{ label: "Mortgage Insurance", value: Number(monthlyPMI), color: "hsl(280, 85%, 65%)" },
			{
				label: "Property Taxes",
				value: Number(formValues.monthlyPropertyTax),
				color: "hsl(170, 95%, 35%)"
			},
			{
				label: "Property Insurance",
				value: Number(formValues.monthlyHomeInsurance),
				color: "hsl(45, 93%, 47%)"
			},
			{
				label: "HOA Dues",
				value: Number(formValues.monthlyHoaDues),
				color: "hsl(355, 78%, 56%)"
			}
		].filter((item) => item.value > 0)
	);
</script>

<Card class="p-6 text-sm" id="payment-calculator">
	<div class="grid grid-cols-1 gap-10 md:grid-cols-2">
		<!-- Chart Column -->
		<div class="col-span-1 flex flex-col gap-5 md:order-2 md:mt-4">
			<!-- Payment Breakdown - Donut Chart -->
			<div class="relative col-span-1 aspect-square">
				<!-- Middle of the chart -->
				<div class="absolute inset-0 flex items-center justify-center">
					<div class="text-center">
						<div class="text-muted-foreground text-sm">Estimated</div>
						<div class="text-3xl font-bold">
							{currency(totalMonthlyPayment).format()}
						</div>
						<div class="text-muted-foreground text-sm">Monthly Payment</div>
					</div>
				</div>

				<!-- Donut Chart -->
				<div
					class="h-full w-full"
					role="img"
					aria-label="Pie chart showing the breakdown of monthly mortgage payments"
					aria-describedby="chartSummary"
				>
					{#if mounted}
						<PieChart
							data={chartData}
							value="value"
							key="label"
							c="label"
							cRange={chartData.map((d) => d.color)}
							innerRadius={-40}
							cornerRadius={10}
							padAngle={0.02}
						>
							<!-- Custom tooltip-->
							{#snippet tooltip(data)}
								<ChartTooltip.Root>
									<ChartTooltip.List>
										<ChartTooltip.Item
											class="border-border bg-background rounded p-2"
											label={chartData.find((d) => d.label === data.label)
												?.label}
											value={currency(
												chartData.find((d) => d.label === data.label)
													?.value ?? 0
											).format()}
											color={chartData.find((d) => d.label === data.label)
												?.color}
										/>
									</ChartTooltip.List>
								</ChartTooltip.Root>
							{/snippet}
						</PieChart>
					{/if}
				</div>
			</div>

			<!-- Payment Breakdown - Text Breakdown -->
			<div id="chartSummary" class="col-span-1 my-auto mt-4 flex flex-col space-y-2">
				{#each chartData as data}
					<div class="flex justify-between">
						<div class="flex items-center gap-2">
							<div
								class="h-3 w-3 rounded-full"
								style:background-color={data.color}
							></div>
							<span>{data.label}</span>
						</div>
						<span class="font-medium">{currency(data.value).format()}</span>
					</div>
				{/each}
			</div>
		</div>

		<!-- Calculator Column -->
		<div
			class="col-span-1 mt-6 flex flex-col gap-5 border-t pt-6 md:mt-0 md:border-t-0 md:pt-0"
		>
			<div class="flex flex-col gap-5">
				<div class="space-y-2">
					<Label for="price">Purchase Price <span class="text-red-500">*</span></Label>
					<div class="relative">
						<Input
							type="string"
							id="price"
							class="pl-8"
							onfocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()}
							onfocusout={stateHandler}
							onkeydown={stateHandler}
							value={currency(formValues.price).format({ symbol: "" })}
						/>
						<span
							class="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2 transform"
							>$</span
						>
					</div>
				</div>

				<div class="space-y-2">
					<Label for="downPaymentAmount"
						>Down Payment<span class="text-red-500">*</span></Label
					>
					<div class="flex flex-row">
						<div class="relative w-3/5">
							<Input
								type="string"
								id="downPaymentAmount"
								class="rounded-none rounded-l-lg border-t border-r-0 border-b border-l pl-8"
								onfocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()}
								onfocusout={stateHandler}
								onkeydown={stateHandler}
								value={currency(formValues.downPaymentAmount).format({
									symbol: ""
								})}
							/>
							<span
								class="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2 transform"
								>$</span
							>
						</div>
						<div class="relative w-2/5">
							<Input
								type="string"
								id="downPaymentPercent"
								class="rounded-none rounded-r-lg border-t border-r border-b border-l"
								onfocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()}
								onfocusout={stateHandler}
								onkeydown={stateHandler}
								value={currency(formValues.downPaymentPercent).format({
									symbol: ""
								})}
							/>
							<span
								class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
								>%</span
							>
						</div>
					</div>
				</div>

				<div class="space-y-2">
					<Label for="loanTerm">Loan Term</Label>
					<select
						id="loanTerm"
						bind:value={formValues.loanTerm}
						class="border-input bg-background w-full rounded border px-3 py-2 text-sm"
					>
						<option value={30}>30-year fixed</option>
						<option value={25}>25-year fixed</option>
						<option value={15}>15-year fixed</option>
						<option value={10}>10-year fixed</option>
					</select>
				</div>

				<div class="space-y-2">
					<Label for="interestRate" class="flex items-center">
						Interest Rate
						<span class="ml-2 text-red-500">*</span>
						<InfoPopover
							content="Representative interest rates are based on Fergmar's data and are for
							  preliminary research purposes only. Actual available rates and monthly
							  payment amounts are subject to market fluctuations and will depend on a
							  number of factors, including your individual financial situation and
							  loan characteristics."
						/>
					</Label>
					<div class="relative">
						<Input
							type="string"
							id="interestRate"
							class="pl-4"
							onfocus={(e: FocusEvent) => (e.target as HTMLInputElement).select()}
							onfocusout={stateHandler}
							onkeydown={stateHandler}
							value={currency(formValues.interestRate).format({ symbol: "" })}
						/>
						<span
							class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
							>%</span
						>
					</div>
				</div>
			</div>

			<Collapsible bind:open={showAdvanced} class="rounded border">
				<CollapsibleTrigger
					class="hover:bg-muted/50 flex w-full items-center justify-between p-4"
				>
					<div class="flex w-full justify-between">
						<span class="font-medium">Show Advanced Options</span>
						<div class="flex items-center gap-4">
							<ChevronDownIcon
								class="h-4 w-4 transition-transform {showAdvanced
									? 'rotate-180'
									: ''}"
								aria-label="Toggle Advanced Options"
							></ChevronDownIcon>
						</div>
					</div>
				</CollapsibleTrigger>
				<CollapsibleContent class="border-t p-6">
					<div class="flex flex-col gap-4">
						<!-- Include PMI Section -->
						<div class="space-y-2">
							<div class="flex flex-row items-center">
								<div class="relative w-3/5 items-center">
									<Checkbox
										class="mr-2"
										id="includePMI"
										bind:checked={includePMI}
										aria-labelledby="includePMI"
									/>
									<label for="includePMI">Inclue PMI</label>
									<InfoPopover
										content="Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details."
									/>
								</div>
								<div class="relative w-2/5">
									<Input
										type="string"
										id="mortgageInsuranceRate"
										onfocus={(e: FocusEvent) =>
											(e.target as HTMLInputElement).select()}
										onfocusout={stateHandler}
										onkeydown={stateHandler}
										value={currency(formValues.mortgageInsuranceRate).format({
											symbol: ""
										})}
									/>
									<span
										class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
										>%</span
									>
								</div>
							</div>
						</div>

						<!-- Include Taxes & Insurance Section -->
						<div class="space-y-2">
							<div class="flex flex-row items-center">
								<Checkbox
									class="mr-2"
									id="includeTaxesInsurance"
									bind:checked={includeTaxesInsurance}
									aria-labelledby="includeTaxesInsurance"
								/>
								<label for="includeTaxesInsurance">Inclue Taxes & Insurance</label>
								<InfoPopover
									content="Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues."
								/>
							</div>
						</div>

						<!-- Property Taxes Section -->
						<div class="space-y-2">
							<Label for="annualPropertyTax"
								>Annual Property Taxes<span class="text-red-500">*</span></Label
							>
							<div class="flex flex-row">
								<div class="relative w-3/5">
									<Input
										type="string"
										id="annualPropertyTax"
										class="rounded-none rounded-l-lg border-t border-r-0 border-b border-l pl-8"
										onfocus={(e: FocusEvent) =>
											(e.target as HTMLInputElement).select()}
										onfocusout={stateHandler}
										onkeydown={stateHandler}
										value={currency(formValues.annualPropertyTax).format({
											symbol: ""
										})}
									/>
									<span
										class="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2 transform"
										>$</span
									>
									<span
										class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
										>/year</span
									>
								</div>
								<div class="relative w-2/5">
									<Input
										type="string"
										id="annualPropertyTaxRate"
										class="rounded-none rounded-r-lg border-t border-r border-b border-l"
										onfocus={(e: FocusEvent) =>
											(e.target as HTMLInputElement).select()}
										onfocusout={stateHandler}
										onkeydown={stateHandler}
										value={currency(formValues.annualPropertyTaxRate).format({
											symbol: ""
										})}
									/>
									<span
										class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
										>%</span
									>
								</div>
							</div>
						</div>

						<!-- Insurance Section -->

						<div class="space-y-2">
							<Label for="annualHomeInsurance"
								>Annual Home Insurance<span class="text-red-500">*</span></Label
							>
							<div class="relative">
								<Input
									type="string"
									id="annualHomeInsurance"
									class="pl-8"
									onfocus={(e: FocusEvent) =>
										(e.target as HTMLInputElement).select()}
									onfocusout={stateHandler}
									onkeydown={stateHandler}
									value={currency(formValues.annualHomeInsurance).format({
										symbol: ""
									})}
								/>
								<span
									class="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2 transform"
									>$</span
								>
								<span
									class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
									>/year</span
								>
							</div>
						</div>

						<!-- HOA Dues Section -->
						<div class="space-y-2">
							<Label for="monthlyHoaDues"
								>HOA Dues<span class="text-red-500">*</span></Label
							>
							<div class="relative">
								<Input
									type="string"
									id="monthlyHoaDues"
									class="pl-8"
									onfocus={(e: FocusEvent) =>
										(e.target as HTMLInputElement).select()}
									onfocusout={stateHandler}
									onkeydown={stateHandler}
									value={currency(formValues.monthlyHoaDues).format({
										symbol: ""
									})}
								/>
								<span
									class="text-muted-foreground absolute top-1/2 left-4 -translate-y-1/2 transform"
									>$</span
								>
								<span
									class="text-muted-foreground absolute top-1/2 right-4 -translate-y-1/2 transform"
									>/month</span
								>
							</div>
						</div>
					</div></CollapsibleContent
				>
			</Collapsible>

			<!-- Amortization Toggle -->
			<div
				class="col-span-1 mt-6 flex flex-col gap-5 border-t pt-6 md:mt-0 md:border-t-0 md:pt-0"
			>
				<div class="flex flex-col gap-5">
					<div class="flex items-center space-x-2">
						<Switch id="showAmortization" bind:checked={showAmortization} />
						<Label for="showAmortization">Show Amortization Schedule</Label>
					</div>
					{#if showAmortization}
						<div class="flex flex-row items-center justify-between gap-2">
							<Label class="text-sm font-normal" for="startDate">Start Date</Label>
							<DatePicker bind:value={startDate} />
						</div>
					{/if}
				</div>
			</div>
		</div>
	</div>

	<div class="col-span-2">
		{#if showAmortization}
			<AmortizationTable
				{startDate}
				{loanAmount}
				annualInterestRate={formValues.interestRate}
				loanTermYears={formValues.loanTerm}
				taxes={formValues.annualPropertyTax}
				insurance={formValues.annualHomeInsurance}
				hoa={formValues.annualHoaDues}
			/>
		{/if}
	</div>

	<!-- Disclaimer -->
	<div class="col-span-2 mt-8 border-t pt-6">
		<p class="text-muted-foreground text-sm text-pretty">
			This mortgage calculator is for informational and educational purposes only. The results
			are estimates based on the information you provide and should not be considered
			financial or lending advice. Actual interest rates, monthly payments, and fees can vary
			based on factors like your credit score, down payment, loan type, and lender. Fergmar
			does not make loans, and this calculator does not constitute a commitment to lend. We
			recommend consulting with a qualified mortgage professional for personalized guidance
			and to obtain accurate loan terms and costs.
		</p>
	</div>
</Card>
